import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  getISOWeek,
  getISOWeekYear,
  parseISO,
  startOfWeek,
  subWeeks,
} from "date-fns";
import { enUS, nb } from "date-fns/locale";
import { projectActivityLine } from "../projectActivityDisplay";

export const ENTRY_MODE_KEY = "promang.hours.entryMode";
export const RECENT_ROWS_KEY = "promang.hours.quick.recentRows";
export const AUTOSAVE_DEBOUNCE_MS = 500;

export const CELL_STATUS = {
  idle: "idle",
  saving: "saving",
  saved: "saved",
  error: "error",
  conflict: "conflict",
};

export function readEntryMode() {
  try {
    return localStorage.getItem(ENTRY_MODE_KEY) === "quick" ? "quick" : "form";
  } catch {
    return "form";
  }
}

export function writeEntryMode(mode) {
  try {
    localStorage.setItem(ENTRY_MODE_KEY, mode === "quick" ? "quick" : "form");
  } catch {
    /* ignore */
  }
}

export function readRecentRows() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_ROWS_KEY) || "[]");
    return Array.isArray(raw) ? raw.slice(0, 12) : [];
  } catch {
    return [];
  }
}

export function pushRecentRow(row) {
  if (!row?.project_id || !row?.project_activity_id) return;
  const next = [
    {
      project_id: row.project_id,
      project_activity_id: row.project_activity_id,
      projectTitle: row.projectTitle || "",
      projectMeta: row.projectMeta || "",
      activityTitle: row.activityTitle || "",
      tag: row.tag || "recent",
    },
    ...readRecentRows().filter(
      (r) =>
        !(
          String(r.project_id) === String(row.project_id) &&
          String(r.project_activity_id) === String(row.project_activity_id)
        )
    ),
  ].slice(0, 12);
  try {
    localStorage.setItem(RECENT_ROWS_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

export function weekBounds(anchorDate) {
  const d =
    typeof anchorDate === "string"
      ? parseISO(anchorDate.substring(0, 10))
      : anchorDate instanceof Date
        ? anchorDate
        : new Date();
  const from = startOfWeek(d, { weekStartsOn: 1 });
  const to = endOfWeek(d, { weekStartsOn: 1 });
  return { from, to, days: eachDayOfInterval({ start: from, end: to }) };
}

export function shiftWeek(anchorDate, delta) {
  const d =
    typeof anchorDate === "string"
      ? parseISO(anchorDate.substring(0, 10))
      : anchorDate instanceof Date
        ? anchorDate
        : new Date();
  return delta >= 0 ? addWeeks(d, delta) : subWeeks(d, Math.abs(delta));
}

export function formatWeekLabel(from, to, locale = nb) {
  const week = getISOWeek(from);
  const year = getISOWeekYear(from);
  const left = format(from, "d.", { locale });
  const right = format(to, "d. MMMM yyyy", { locale });
  return { week, year, rangeText: `${left}–${right}`, short: `Uke ${week}` };
}

export function dayHeaderParts(day, locale = nb) {
  return {
    dateKey: format(day, "yyyy-MM-dd"),
    num: format(day, "dd"),
    label: format(day, "EEE", { locale }).replace(/\.$/, "").toLowerCase(),
    isWeekend: day.getDay() === 0 || day.getDay() === 6,
    isToday: format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd"),
  };
}

export function parseHoursInput(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim().replace(/\s/g, "").replace(",", ".");
  if (!s || s === "-" || s === "–") return null;
  // hours:minutes
  if (/^\d+:\d{1,2}$/.test(s)) {
    const [h, m] = s.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return Math.min(24, Math.max(0, h + m / 60));
  }
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (Number.isNaN(n)) return null;
  return Math.min(24, Math.max(0, n));
}

export function formatHoursDisplay(value, { decimal = true } = {}) {
  if (value == null || value === "" || Number(value) === 0) return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  if (!decimal) {
    const h = Math.floor(n);
    const m = Math.round((n - h) * 60);
    if (m === 0) return String(h);
    return `${h}:${String(m).padStart(2, "0")}`;
  }
  const rounded = Math.round(n * 100) / 100;
  return String(rounded).replace(".", ",");
}

export function formatHoursSum(value) {
  const n = Number(value) || 0;
  return (Math.round(n * 10) / 10).toFixed(1).replace(".", ",");
}

export function weekdayKey(dateObj) {
  return format(dateObj, "EEEE", { locale: enUS }).toLowerCase();
}

export function normalHoursForWeek(days, scaledWeekdays) {
  if (!scaledWeekdays || typeof scaledWeekdays !== "object") return null;
  let sum = 0;
  days.forEach((day) => {
    const key = weekdayKey(day);
    const v = parseFloat(scaledWeekdays[key] ?? 0);
    if (!Number.isNaN(v)) sum += v;
  });
  return sum;
}

export function projectTitleOf(hourOrProject) {
  const p = hourOrProject?.project || hourOrProject;
  if (!p) return "";
  return (
    p.displayTitle ||
    p.display_title ||
    p.name ||
    [p.project_number, p.name].filter(Boolean).join(" ") ||
    ""
  );
}

export function projectMetaOf(hourOrProject) {
  const p = hourOrProject?.project || hourOrProject;
  if (!p) return "";
  return p.customer?.displayTitle || p.customer?.name || p.status || "";
}

export function activityTitleOf(hour) {
  return projectActivityLine(hour?.project_activity) || "";
}

export function rowKeyFromIds(projectId, activityId) {
  return `${projectId ?? "p"}:${activityId ?? "a"}`;
}

/**
 * Group week hours into grid rows (project_activity × days).
 * Multiple hours on same activity/day → conflict cell (sum + open form).
 */
export function buildRowsFromHours(hours, dayKeys) {
  const map = new Map();
  (hours || []).forEach((hour) => {
    const paId = hour.project_activity?.id ?? hour.project_activity_id;
    const projectId = hour.project?.id ?? hour.project_id;
    if (paId == null && projectId == null) return;
    const key = rowKeyFromIds(projectId, paId);
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        project_id: projectId,
        project_activity_id: paId,
        projectTitle: projectTitleOf(hour),
        projectMeta: projectMetaOf(hour),
        activityTitle: activityTitleOf(hour),
        cells: Object.fromEntries(
          dayKeys.map((d) => [
            d,
            {
              date: d,
              hourId: null,
              time: null,
              draft: "",
              description: "",
              status: CELL_STATUS.idle,
              error: null,
              conflictHours: null,
            },
          ])
        ),
      });
    }
    const row = map.get(key);
    const dateKey = String(hour.date || "").substring(0, 10);
    if (!row.cells[dateKey]) return;
    const cell = row.cells[dateKey];
    const t = parseFloat(hour.time) || 0;
    if (cell.hourId != null || cell.conflictHours) {
      const list = cell.conflictHours
        ? [...cell.conflictHours]
        : [
            {
              id: cell.hourId,
              time: cell.time,
              description: cell.description,
            },
          ];
      list.push({
        id: hour.id,
        time: t,
        description: hour.description || "",
      });
      const sum = list.reduce((a, h) => a + (parseFloat(h.time) || 0), 0);
      row.cells[dateKey] = {
        ...cell,
        hourId: null,
        time: sum,
        draft: formatHoursDisplay(sum),
        description: "",
        conflictHours: list,
        status: CELL_STATUS.conflict,
      };
    } else {
      row.cells[dateKey] = {
        ...cell,
        hourId: hour.id,
        time: t,
        draft: formatHoursDisplay(t),
        description: hour.description || "",
        status: CELL_STATUS.idle,
        conflictHours: null,
      };
    }
  });
  return Array.from(map.values());
}

export function emptyCell(date) {
  return {
    date,
    hourId: null,
    time: null,
    draft: "",
    description: "",
    status: CELL_STATUS.idle,
    error: null,
    conflictHours: null,
  };
}

export function makeEmptyRow(dayKeys, partial = {}) {
  const projectId = partial.project_id ?? null;
  const activityId = partial.project_activity_id ?? null;
  const id =
    partial.id ||
    (projectId && activityId
      ? rowKeyFromIds(projectId, activityId)
      : `draft-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
  return {
    id,
    project_id: projectId,
    project_activity_id: activityId,
    projectTitle: partial.projectTitle || "",
    projectMeta: partial.projectMeta || "",
    activityTitle: partial.activityTitle || "",
    cells: Object.fromEntries(dayKeys.map((d) => [d, emptyCell(d)])),
  };
}
